import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { intakeFormSet, intakeFormValidate } from '~/actions/intakeForm';
import { ticketDocumentVersionsSet, ticketUpdate } from '~/actions/ticket';
import { getTicketDocumentVersions } from '~/api/tickets';
import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { usePageSidebarContext } from '~/eds/src/components/PageSidebar/PageSidebarContext';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Stepper } from '~/eds/src/components/Stepper/Stepper';
import { Action } from '~/eds/src/types/Action';
import { IconType } from '~/eds/src/types/IconType';
import { Uuid } from '~/eds/src/types/Uuid';
import { DEPRECATED_actionsToUserActions } from '~/eds/src/utils/DEPRECATED_actionsToUserActions';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import { isFormValid } from '~/utils/intakeForm';
import { prepareIntakeFormSubmitData } from '~/utils/workflow';

import { INTAKE_FORM_CONTAINER_ID, INTAKE_FORM_SECTION_ID } from './constants';
import { WorkflowForm } from './WorkflowForm/WorkflowForm';

interface Props {
  ticketId: Uuid;
  title: string;
  actions?: Action[];
}

export const TicketIntakeForm = ({ actions = [], ticketId, title }: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { toast } = useToast();
  const { isPanelEnlarged } = useAppConfigContext();
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const isWSSIntegrationPhase2M1M3EnabledRef = useRef(
    isWSSIntegrationPhase2M1M3Enabled,
  );

  const {
    data,
    isLoading,
    error,
    refetch: refetchIntakeForm,
  } = api.endpoints.getTicketIntakeForm.useQuery(ticketId);
  const [updateTicket, { isLoading: isSubmittingForm }] =
    api.endpoints.putTicket.useMutation();
  const {
    data: envelope,
    error: envelopeError,
    isLoading: isLoadingEnvelope,
  } = api.endpoints.getEnvelope.useQuery(
    {
      ticketId,
    },
    {
      skip: !isEsignatureV2RevampEnabled,
    },
  );
  const {
    data: envelopeCheck,
    error: envelopeCheckError,
    isLoading: isLoadingEnvelopeCheck,
  } = api.endpoints.checkEnvelope.useQuery(ticketId, {
    skip: isEsignatureV2RevampEnabled,
  });

  const pageSidebarContext = usePageSidebarContext();
  const intakeForm = useSelector(selectors.selectIntakeForm);
  const ticket = useSelector(selectors.selectTicket);

  const [isEditing, setIsEditing] = useState(false);
  const shouldDisableEditIntakeForm =
    ticket.status === TicketStatusType.Completed ||
    ticket.stage === TicketStageType.Finalize ||
    ticket.isCancelled ||
    isLoading ||
    isLoadingEnvelope ||
    isLoadingEnvelopeCheck;

  const hasEnvelope = Boolean(envelope ?? envelopeCheck?.data);

  useEffect(() => {
    if (data) {
      dispatch(
        intakeFormSet({
          res: { ...data },
          isWSSIntegrationPhase2M1M3Enabled:
            isWSSIntegrationPhase2M1M3EnabledRef.current,
        }),
      );
    }
  }, [data, dispatch]);

  const performSave = async () => {
    const { fields } = intakeForm;
    const payload = prepareIntakeFormSubmitData({ fields });
    try {
      const res = await updateTicket({
        ticketId,
        ...payload,
      }).unwrap();
      if (res) {
        // update versions in case it was a template submission
        if (!res.wasManuallyUpdated) {
          const upToDateVersions = await getTicketDocumentVersions(
            res.document.id,
          );
          dispatch(ticketDocumentVersionsSet(upToDateVersions));
        }
        // May not need esigErrorMessage
        const esigErrorMessage = res?.esigErrorMessage;
        refetchIntakeForm();
        dispatch(
          ticketUpdate({
            document: res.document,
            stages: res.stages,
            modifiedDate: res.modifiedDate,
            name: res.name,
          }),
        );
        setIsEditing(false);

        esigErrorMessage
          ? toast({
              message: esigErrorMessage,
              status: 'info',
            })
          : toast({
              message: intl.formatMessage({
                id: 'IntakeForm.ticketUpdateSuccess',
                defaultMessage: 'Ticket has been successfully updated',
                description:
                  'Success toast message displayed when intake form changes are saved successfully',
              }),
              status: 'success',
            });
      }
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'IntakeForm.ticketUpdateError',
          defaultMessage: 'An error occurred while updating a ticket.',
          description:
            'Error toast message displayed when intake form save operation fails',
        }),
        status: 'danger',
      });
    }
  };

  const onFormSave = async () => {
    dispatch(intakeFormValidate());

    if (isFormValid(intakeForm)) {
      if (hasEnvelope) {
        showResetSignaturesModal();
      } else {
        await performSave();
      }
    } else {
      toast({
        message: intl.formatMessage({
          id: 'IntakeForm.validationError',
          defaultMessage: 'Please fill in all fields correctly.',
          description:
            'Warning toast message displayed when required intake form fields are missing or invalid',
        }),
        status: 'warning',
      });
    }
  };

  const onCancel = () => {
    dispatch(
      intakeFormSet({
        res: { ...data },
        isWSSIntegrationPhase2M1M3Enabled:
          isWSSIntegrationPhase2M1M3EnabledRef.current,
      }),
    );
    setIsEditing(false);
  };

  const [
    resetSignaturesModal,
    showResetSignaturesModal,
    hideResetSignaturesModal,
  ] = useModal({
    title: intl.formatMessage({
      id: 'IntakeForm.resetSignaturesModalTitle',
      defaultMessage: 'Reset Signatures?',
      description:
        'Confirmation modal title asking user to confirm resetting all signatures when updating intake form with existing envelope',
    }),
    children: intl.formatMessage({
      id: 'IntakeForm.resetSignaturesModalMessage',
      defaultMessage:
        'Updating an intake form for a document that has already been processed can result in mismatched signer information. All existing signatures will be voided. The signature coordinator must collect signatures again.',
      description:
        'Confirmation modal body text warning user that updating intake form will void all existing signatures and require re-collection',
    }),
    primaryAction: {
      text: intl.formatMessage({
        id: 'IntakeForm.resetSignaturesModalConfirm',
        defaultMessage: 'Update Form & Reset Signatures',
        description:
          'Primary danger button text to confirm saving intake form changes and voiding all existing signatures',
      }),
      variant: 'danger',
      isLoading: isSubmittingForm,
      onClick: () => {
        performSave();
        hideResetSignaturesModal();
      },
    },
    cancelText: intl.formatMessage({
      id: 'IntakeForm.resetSignaturesModalCancel',
      defaultMessage: 'Cancel',
      description:
        'Cancel button text to close reset signatures confirmation modal without saving changes',
    }),
  });

  const intakeFormActions = [
    ...(!isEditing
      ? [
          {
            disabled: shouldDisableEditIntakeForm,
            icon: 'edit' as IconType,
            label: intl.formatMessage({
              id: 'IntakeForm.editFormLabel',
              defaultMessage: 'Edit Form',
              description:
                'Button label to enable editing mode for intake form',
            }),
            value: 'Edit Form',
            onClick: () => setIsEditing(true),
          },
        ]
      : []),
    ...actions,
  ];

  const footerActions = [
    {
      disabled: isSubmittingForm,
      text: intl.formatMessage({
        id: 'IntakeForm.cancelButton',
        defaultMessage: 'Cancel',
        description:
          'Button text to discard intake form changes and exit editing mode',
      }),
      level: 'tertiary' as const,
      onClick: onCancel,
    },
    {
      isLoading: isSubmittingForm,
      text: intl.formatMessage({
        id: 'IntakeForm.saveChangesButton',
        defaultMessage: 'Save Changes',
        description:
          'Primary button text to save and apply intake form changes to the ticket',
      }),
      mode: 'submit' as const,
      level: 'primary' as const,
      onClick: onFormSave,
    },
  ];

  const formStatusMessage = useMemo(() => {
    if (shouldDisableEditIntakeForm) {
      return (
        <StatusMessage
          message={intl.formatMessage({
            id: 'IntakeForm.disabledFormMessage',
            defaultMessage:
              'The contract has been signed or is completed. No changes can be made to the intake form.',
            description:
              'Warning message body explaining why intake form cannot be edited when ticket is completed, finalized, or cancelled',
          })}
          status="warning"
          title={intl.formatMessage({
            id: 'IntakeForm.disabledFormTitle',
            defaultMessage: 'Changes cannot be made to the intake form',
            description:
              'Warning message title when intake form editing is disabled due to ticket status',
          })}
        />
      );
    } else if (ticket.wasManuallyUpdated) {
      return (
        <StatusMessage
          message={intl.formatMessage({
            id: 'IntakeForm.manuallyUpdatedMessage',
            defaultMessage:
              'Uploading breaks the link between the document and the fields below. If changes are required, you will have to edit the document directly and upload a new version.',
            description:
              'Warning message body explaining that intake form changes will not update the document when it was manually uploaded',
          })}
          status="warning"
          title={intl.formatMessage({
            id: 'IntakeForm.manuallyUpdatedTitle',
            defaultMessage:
              'Changes made will not be reflected in the document.',
            description:
              'Warning message title when ticket document was manually uploaded and is no longer linked to intake form',
          })}
        />
      );
    } else if (hasEnvelope) {
      return (
        <StatusMessage
          status="warning"
          message={intl.formatMessage({
            id: 'IntakeForm.envelopeWarningMessage',
            defaultMessage:
              'Any changes made to the intake form will not be applied to the document',
            description:
              'Warning message displayed when ticket has an active signature envelope and intake form changes will not update the document',
          })}
          onDismiss={() => {}}
        />
      );
    } else {
      return <></>;
    }
  }, [ticket, shouldDisableEditIntakeForm, hasEnvelope]);

  const form = (
    <Layout
      borderLeft={isPanelEnlarged ? 'border' : undefined}
      direction="column"
      h="100%"
      id={INTAKE_FORM_CONTAINER_ID}
      overflowY="auto"
      spacing={2}
      w="100%"
    >
      <Box py={2} px={4}>
        {formStatusMessage}
      </Box>
      <WorkflowForm
        isEditing={isEditing && !isSubmittingForm}
        ticketId={ticketId}
      />
    </Layout>
  );

  const steps = useMemo(() => {
    if (!intakeForm.sections) return [];

    return Object.values(intakeForm.sections)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((section) => ({
        id: section.name,
        label: section.name,
        description: section.description,
        pathname: '',
      }));
  }, [intakeForm.sections]);

  const content = isPanelEnlarged ? (
    <Layout h="100%">
      <Box px={6} py={4}>
        <Stepper
          containerId={INTAKE_FORM_CONTAINER_ID}
          currentStepId={steps[0]?.id}
          name={intl.formatMessage({
            id: 'IntakeForm.stepperName',
            defaultMessage: 'intake form steps',
            description:
              'Accessible name for stepper navigation component that allows jumping between intake form sections',
          })}
          steps={steps}
          stepClassName={INTAKE_FORM_SECTION_ID}
        />
      </Box>
      {form}
    </Layout>
  ) : (
    form
  );

  return (
    <Panel
      enableMaximize
      expandWidth="680px"
      width="560px"
      onWidthChange={(w) => pageSidebarContext?.setEffectiveWidth?.(w)}
      actions={DEPRECATED_actionsToUserActions(intakeFormActions)}
      enableContentPadding={false}
      footer={isEditing ? { actions: footerActions } : undefined}
      loadingContent={{ isLoading }}
      placeholderContent={
        error || envelopeError || envelopeCheckError
          ? {
              icon: 'checklist',
              message: intl.formatMessage({
                id: 'IntakeForm.fetchError',
                defaultMessage:
                  'An error occurred while fetching an intake form.',
                description:
                  'Error placeholder message displayed when API call to load intake form data fails',
              }),
            }
          : undefined
      }
      title={title}
    >
      {content}
      {resetSignaturesModal}
    </Panel>
  );
};
